<template>
    <VJoyDialog ref="dialogRef">
        <template #dialog-header>{{ t('profile.section.portfolio.share.dialog.title') }}</template>
        <template #dialog-body>
            <VJoyWrapper direction="column" row-gap="6" align="stretch" no-margin style="margin-top: var(--joy-core-spacing-5)">
                <div>
                    <VJoyLabel class="joy-mb-2" :text="t('profile.section.portfolio.share.dialog.social.label')" />
                    <VJoyWrapper no-margin>
                        <VJoyIconButton
                            data-testid="portfolio-share-dialog-linkedin"
                            size="small"
                            variant="secondary"
                            icon="linkedin"
                            :label="t('profile.section.portfolio.share.dialog.social.linkedin.label')"
                            @click="handleShareOnNetwork('linkedin')"
                        />
                        <VJoyIconButton
                            data-testid="portfolio-share-dialog-facebook"
                            size="small"
                            variant="secondary"
                            icon="facebook"
                            :label="t('profile.section.portfolio.share.dialog.social.facebook.label')"
                            @click="handleShareOnNetwork('facebook')"
                        />
                    </VJoyWrapper>
                </div>
                <div>
                    <VJoyLabel class="joy-mb-2" :text="t('profile.section.portfolio.share.dialog.link.label')" />
                    <VJoyWrapper align="center" no-margin class="portfolio-share-dialog__link-row">
                        <VJoyInput data-testid="portfolio-share-dialog-url-input" :model-value="shareUrl" readonly />
                        <VJoyButton data-testid="portfolio-share-dialog-copy-button" size="small" variant="primary" @click="handleCopy">
                            {{ t('generic.action.copy') }}
                        </VJoyButton>
                    </VJoyWrapper>
                </div>
            </VJoyWrapper>
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {useTemplateRef} from 'vue';
    import {useTranslation} from '#imports';
    import {copyToClipboard} from '@malt/dom';
    import {safelyNavigateTo} from '@malt/utils';
    import {VJoyButton, VJoyDialog, VJoyIconButton, VJoyInput, VJoyLabel, VJoyWrapper, pushVJoySnackbar} from '@maltjoy/core-vue';
    import {useProfileTracking} from '~/composables/useProfileTracking';

    const props = defineProps<{shareUrl: string; portfolioItemId?: string}>();

    const {t} = useTranslation();
    const dialogRef = useTemplateRef('dialogRef');
    const {trackPortfolioSocialShareOpened, trackPortfolioSocialShareStarted} = useProfileTracking();

    function handleCopy() {
        copyToClipboard(props.shareUrl);
        pushVJoySnackbar({props: {message: t('copy.to.clipboard.message')}});
        trackPortfolioSocialShareStarted('direct_link', props.portfolioItemId);
    }

    function handleShareOnNetwork(network: 'linkedin' | 'facebook') {
        const encodedUrl = encodeURIComponent(props.shareUrl);
        const url =
            network === 'linkedin'
                ? `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}`
                : `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        safelyNavigateTo(url, {externalToThisHost: true, open: {target: '_blank'}});
        trackPortfolioSocialShareStarted(network, props.portfolioItemId);
    }

    function show() {
        trackPortfolioSocialShareOpened(props.portfolioItemId);
        dialogRef.value?.show();
    }

    defineExpose({show});
</script>

<style scoped lang="scss">
    .portfolio-share-dialog__link-row {
        :deep(.joy-input--medium) {
            flex-grow: 1;
        }
    }
</style>
